import React, { useState } from 'react';
import { format } from 'date-fns';
import apiService from '../services/api';

interface Article {
  id: number;
  title: string;
  content: string;
  url: string;
  publishedAt: string;
  scrapedAt: string;
  source: {
    name: string;
  };
}

interface ModelConfig {
  name: string;
  model: string;
  tier: number;
  color: string;
}

interface ComparisonResult {
  modelName: string;
  loading: boolean;
  error: string | null;
  result: {
    categoryScores: Record<string, number>;
    overallRiskScore: number;
    confidenceLevel: number;
    urgencyScore: number;
    keyEntities: string[];
    keyPoints: string[];
    summary: string;
    reasoning: any;
    threatActorMentions: string[];
    tokensUsed: number;
    cost: number;
  } | null;
}

interface ModelComparisonModalProps {
  article: Article;
  onClose: () => void;
}

const MODEL_CONFIGS: ModelConfig[] = [
  {
    name: 'Claude 3.5 Haiku',
    model: 'anthropic/claude-3.5-haiku',
    tier: 2,
    color: 'bg-blue-100 text-blue-800'
  },
  {
    name: 'Claude Sonnet 4',
    model: 'anthropic/claude-sonnet-4',
    tier: 2,
    color: 'bg-purple-100 text-purple-800'
  },
  {
    name: 'Gemini 2.5 Pro',
    model: 'google/gemini-2.5-pro',
    tier: 2,
    color: 'bg-teal-100 text-teal-800'
  },
  {
    name: 'Gemini 2.5 Flash',
    model: 'google/gemini-2.5-flash',
    tier: 2,
    color: 'bg-emerald-100 text-emerald-800'
  }
];

export function ModelComparisonModal({ article, onClose }: ModelComparisonModalProps) {
  const [selectedModels, setSelectedModels] = useState<string[]>([MODEL_CONFIGS[0].name, MODEL_CONFIGS[1].name]);
  const [results, setResults] = useState<ComparisonResult[]>([]);
  const [comparing, setComparing] = useState(false);

  const handleModelToggle = (modelName: string) => {
    setSelectedModels(prev => 
      prev.includes(modelName) 
        ? prev.filter(m => m !== modelName)
        : [...prev, modelName]
    );
  };

  const runComparison = async () => {
    if (selectedModels.length === 0) return;

    setComparing(true);
    const initialResults: ComparisonResult[] = selectedModels.map(modelName => ({
      modelName,
      loading: true,
      error: null,
      result: null
    }));
    setResults(initialResults);

    // Run all models in parallel
    const promises = selectedModels.map(async (modelName) => {
      const modelConfig = MODEL_CONFIGS.find(m => m.name === modelName)!;
      
      try {
        const result = await apiService.compareModels(article.id, {
          model: modelConfig.model,
          tier: modelConfig.tier
        });
        
        return {
          modelName,
          loading: false,
          error: null,
          result
        };
      } catch (error) {
        return {
          modelName,
          loading: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          result: null
        };
      }
    });

    const completedResults = await Promise.all(promises);
    setResults(completedResults);
    setComparing(false);
  };

  const formatCost = (cost: number) => `$${cost.toFixed(6)}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-7xl max-h-[90vh] overflow-auto">
        <div className="mb-6">
          <h3 className="text-xl font-semibold mb-2">Model Comparison</h3>
          <div className="text-sm text-gray-600 space-y-1">
            <p><strong>Article:</strong> {article.title}</p>
            <p><strong>Source:</strong> {article.source.name}</p>
            <p><strong>Published:</strong> {format(new Date(article.publishedAt), 'PPpp')}</p>
          </div>
        </div>

        {/* Model Selection */}
        <div className="mb-6">
          <h4 className="font-medium mb-3">Select Models to Compare:</h4>
          <div className="grid grid-cols-2 gap-3">
            {MODEL_CONFIGS.map(config => (
              <label key={config.name} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedModels.includes(config.name)}
                  onChange={() => handleModelToggle(config.name)}
                  className="rounded border-gray-300"
                />
                <span className={`px-2 py-1 rounded text-sm ${config.color}`}>
                  {config.name}
                </span>
                <span className="text-xs text-gray-500">({config.model})</span>
              </label>
            ))}
          </div>
        </div>

        {/* Run Comparison Button */}
        <div className="mb-6">
          <button
            onClick={runComparison}
            disabled={comparing || selectedModels.length === 0}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {comparing ? '🔄 Comparing...' : `🚀 Compare ${selectedModels.length} Models`}
          </button>
          <p className="text-xs text-gray-500 mt-1">
            * Results are not saved to database
          </p>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div className="space-y-6">
            <h4 className="font-medium">Comparison Results:</h4>
            
            {/* Cost Summary */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h5 className="font-medium mb-2">Cost Comparison:</h5>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {results.map(result => (
                  <div key={result.modelName} className="text-center">
                    <div className="font-medium">{result.modelName}</div>
                    <div className="text-lg font-bold text-blue-600">
                      {result.result ? formatCost(result.result.cost) : result.loading ? '...' : 'Error'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {result.result ? `${result.result.tokensUsed} tokens` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Side-by-side Results */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {results.map(result => {
                const config = MODEL_CONFIGS.find(m => m.name === result.modelName)!;
                
                return (
                  <div key={result.modelName} className="border rounded-lg p-4">
                    <div className={`inline-block px-3 py-1 rounded text-sm font-medium mb-4 ${config.color}`}>
                      {result.modelName}
                    </div>
                    
                    {result.loading && (
                      <div className="text-center py-8">
                        <div className="animate-spin text-2xl">🔄</div>
                        <p className="text-sm text-gray-500 mt-2">Analyzing...</p>
                      </div>
                    )}
                    
                    {result.error && (
                      <div className="bg-red-50 border border-red-200 rounded p-3">
                        <p className="text-red-600 font-medium">Error:</p>
                        <p className="text-red-500 text-sm">{result.error}</p>
                      </div>
                    )}
                    
                    {result.result && (
                      <div className="space-y-4">
                        {/* Overall Risk */}
                        <div className="text-center">
                          <div className="text-3xl font-bold text-blue-600">
                            {result.result.overallRiskScore}/10
                          </div>
                          <div className="text-sm text-gray-500">Overall Risk</div>
                        </div>
                        
                        {/* Category Scores */}
                        <div>
                          <h6 className="font-medium mb-2">Category Scores:</h6>
                          <div className="space-y-1">
                            {Object.entries(result.result.categoryScores).map(([category, score]) => (
                              <div key={category} className="flex justify-between text-sm">
                                <span>{category.replace(/_/g, ' ')}</span>
                                <span className="font-medium">{score}/10</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Key Points */}
                        {result.result.keyPoints.length > 0 && (
                          <div>
                            <h6 className="font-medium mb-2">Key Points:</h6>
                            <ul className="text-sm space-y-1">
                              {result.result.keyPoints.map((point, idx) => (
                                <li key={idx} className="flex items-start">
                                  <span className="text-gray-400 mr-2">•</span>
                                  <span>{point}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Summary */}
                        <div>
                          <h6 className="font-medium mb-2">Summary:</h6>
                          <p className="text-sm text-gray-600">{result.result.summary}</p>
                        </div>
                        
                        {/* Threat Actors */}
                        {result.result.threatActorMentions.length > 0 && (
                          <div>
                            <h6 className="font-medium mb-2">Threat Actors:</h6>
                            <p className="text-sm text-gray-600">
                              {result.result.threatActorMentions.join(', ')}
                            </p>
                          </div>
                        )}
                        
                        {/* Metrics */}
                        <div className="text-xs text-gray-500 space-y-1">
                          <div>Confidence: {(result.result.confidenceLevel * 100).toFixed(1)}%</div>
                          <div>Urgency: {(result.result.urgencyScore * 100).toFixed(1)}%</div>
                          <div>Cost: {formatCost(result.result.cost)}</div>
                          <div>Tokens: {result.result.tokensUsed}</div>
                          <div className="pt-2">
                            <button
                              onClick={() => {
                                if (!result.result) return;
                                const rawOutput = {
                                  reasoning: result.result.reasoning,
                                  categoryScores: result.result.categoryScores,
                                  overallRiskScore: result.result.overallRiskScore,
                                  confidenceLevel: result.result.confidenceLevel,
                                  urgencyScore: result.result.urgencyScore,
                                  keyEntities: result.result.keyEntities,
                                  keyPoints: result.result.keyPoints,
                                  summary: result.result.summary,
                                  threatActorMentions: result.result.threatActorMentions,
                                  tokensUsed: result.result.tokensUsed,
                                  cost: result.result.cost
                                };
                                navigator.clipboard.writeText(JSON.stringify(rawOutput, null, 2));
                                alert('Raw AI output copied to clipboard!');
                              }}
                              className="text-blue-600 hover:text-blue-800 underline"
                            >
                              📋 Copy Raw Output
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Close Button */}
        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}